import { IMapTemplateRecord, ITemplateSpot } from '../interfaces/mapTemplate';

/**
 * The minimal branding-spot shape the drift engine reads. Both the admin
 * `IBrandingSpot` and the hub `IBrandingSpotApi` structurally satisfy this, so
 * the engine stays the single source of truth without depending on either
 * app's full type.
 */
export interface IDriftBrandingSpot {
  ueIdentifier?: string;
  type?: string;
  name?: string;
  data?: { url?: string; location?: string };
}

const normalizeUe = (ue?: string | null): string => (ue ?? '').trim();

export const brandingSpotHasMedia = (
  spot?: IDriftBrandingSpot | null,
): boolean => {
  if (!spot) {
    return false;
  }
  const url = spot.data?.url;
  const location = spot.data?.location;
  return Boolean((url && url.trim()) || (location && location.trim()));
};

export type BrandingSpotDriftStatus =
  | 'configured'
  | 'placeholder'
  | 'missing'
  | 'orphaned';

export type BrandingDriftState =
  | 'up-to-date'
  | 'behind'
  | 'outdated'
  | 'unknown';

export interface IBrandingSpotDriftRow<
  TSpot extends IDriftBrandingSpot = IDriftBrandingSpot,
> {
  ueIdentifier: string;
  status: BrandingSpotDriftStatus;
  isNew: boolean;
  isRemoved: boolean;
  type: string;
  regionName?: string;
  brandingSpot?: TSpot;
  templateSpot?: ITemplateSpot;
}

export interface IBrandingDrift<
  TSpot extends IDriftBrandingSpot = IDriftBrandingSpot,
> {
  hasTemplate: boolean;
  templateVersion?: string;
  templateSpotCount: number;
  configured: number;
  placeholders: number;
  missing: number;
  orphaned: number;
  newAvailable: number;
  removedPresent: number;
  mappedPct: number;
  state: BrandingDriftState;
  behindCount: number;
  rows: IBrandingSpotDriftRow<TSpot>[];
}

const changesetNum = (template: IMapTemplateRecord): number => {
  const parsed = Number((template.sourceChangeset || '').replace(/\D/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const templateOrderValue = (template: IMapTemplateRecord): number =>
  template.timestamp ?? changesetNum(template);

/**
 * The version directly preceding the active one (by ingestion order). Spots
 * present in the active template but absent here are "new in this version";
 * spots present here but gone from active were "removed in this version".
 */
export const pickPreviousTemplate = (
  templates: IMapTemplateRecord[],
  active?: IMapTemplateRecord | null,
): IMapTemplateRecord | null => {
  if (!active) {
    return null;
  }
  const activeOrder = templateOrderValue(active);
  const older = templates
    .filter(
      (template) =>
        template.mapTemplateId !== active.mapTemplateId &&
        templateOrderValue(template) < activeOrder,
    )
    .sort((a, b) => templateOrderValue(b) - templateOrderValue(a));
  return older[0] ?? null;
};

/**
 * Runs `worker` over `items` with at most `limit` in flight at once, draining
 * continuously (a finished slot immediately picks up the next item).
 */
export const runWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let cursor = 0;
  const pool = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        results[index] = await worker(items[index], index);
      }
    },
  );
  await Promise.all(pool);
  return results;
};

/** Active-template spot identifiers, built once and reused across brandings. */
export const buildTemplateSpotKeySet = (
  activeTemplate?: IMapTemplateRecord | null,
): Set<string> => {
  const keys = new Set<string>();
  (activeTemplate?.spots ?? []).forEach((spot) => {
    const ue = normalizeUe(spot.ueIdentifier);
    if (ue) {
      keys.add(ue);
    }
  });
  return keys;
};

/**
 * Cheap per-branding drift for list badges: set-diff only, no row allocation.
 * Pass a `templateKeys` set built once via {@link buildTemplateSpotKeySet}.
 */
export const computeBrandingDriftBadge = (
  brandingSpots: IDriftBrandingSpot[],
  templateKeys: Set<string>,
): { state: BrandingDriftState; behindCount: number } => {
  if (templateKeys.size === 0) {
    return { state: 'unknown', behindCount: 0 };
  }
  const brandingKeys = new Set<string>();
  brandingSpots.forEach((spot) => {
    const ue = normalizeUe(spot.ueIdentifier);
    if (ue) {
      brandingKeys.add(ue);
    }
  });
  let missing = 0;
  templateKeys.forEach((ue) => {
    if (!brandingKeys.has(ue)) {
      missing += 1;
    }
  });
  let orphaned = 0;
  brandingKeys.forEach((ue) => {
    if (!templateKeys.has(ue)) {
      orphaned += 1;
    }
  });
  const state: BrandingDriftState =
    orphaned > 0 ? 'outdated' : missing > 0 ? 'behind' : 'up-to-date';
  return { state, behindCount: missing };
};

export const computeBrandingSpotDrift = <
  TSpot extends IDriftBrandingSpot = IDriftBrandingSpot,
>(
  brandingSpots: TSpot[],
  activeTemplate?: IMapTemplateRecord | null,
  previousTemplate?: IMapTemplateRecord | null,
): IBrandingDrift<TSpot> => {
  const templateSpots = activeTemplate?.spots ?? [];
  const hasTemplate = Boolean(activeTemplate) && templateSpots.length > 0;

  const templateByUe = new Map<string, ITemplateSpot>();
  templateSpots.forEach((spot) => {
    const ue = normalizeUe(spot.ueIdentifier);
    if (ue) {
      templateByUe.set(ue, spot);
    }
  });

  const previousSet = new Set(
    (previousTemplate?.spots ?? []).map((spot) =>
      normalizeUe(spot.ueIdentifier),
    ),
  );

  const newInActive = new Set<string>();
  if (previousTemplate) {
    templateByUe.forEach((_spot, ue) => {
      if (!previousSet.has(ue)) {
        newInActive.add(ue);
      }
    });
  }

  const removedInActive = new Set<string>();
  if (previousTemplate) {
    previousSet.forEach((ue) => {
      if (ue && !templateByUe.has(ue)) {
        removedInActive.add(ue);
      }
    });
  }

  const rows: IBrandingSpotDriftRow<TSpot>[] = [];
  const coveredUe = new Set<string>();
  let configured = 0;
  let placeholders = 0;
  let orphaned = 0;
  let removedPresent = 0;

  brandingSpots.forEach((spot) => {
    const ue = normalizeUe(spot.ueIdentifier);
    if (ue) {
      coveredUe.add(ue);
    }
    const inTemplate = ue ? templateByUe.has(ue) : false;
    const hasMedia = brandingSpotHasMedia(spot);
    const templateSpot = ue ? templateByUe.get(ue) : undefined;

    let status: BrandingSpotDriftStatus;
    if (hasTemplate && !inTemplate) {
      status = 'orphaned';
    } else if (hasMedia) {
      status = 'configured';
    } else {
      status = 'placeholder';
    }

    const isRemoved = status === 'orphaned' && removedInActive.has(ue);
    if (status === 'configured') {
      configured += 1;
    } else if (status === 'placeholder') {
      placeholders += 1;
    } else {
      orphaned += 1;
      if (isRemoved) {
        removedPresent += 1;
      }
    }

    rows.push({
      ueIdentifier: ue || spot.ueIdentifier || '',
      status,
      isNew: false,
      isRemoved,
      type: spot.type || templateSpot?.type || 'IMAGE',
      regionName: templateSpot?.regionName,
      brandingSpot: spot,
      templateSpot,
    });
  });

  let missing = 0;
  let newAvailable = 0;
  templateByUe.forEach((templateSpot, ue) => {
    if (coveredUe.has(ue)) {
      return;
    }
    missing += 1;
    const isNew = newInActive.has(ue);
    if (isNew) {
      newAvailable += 1;
    }
    rows.push({
      ueIdentifier: ue,
      status: 'missing',
      isNew,
      isRemoved: false,
      type: templateSpot.type || 'IMAGE',
      regionName: templateSpot.regionName,
      templateSpot,
    });
  });

  const templateSpotCount = templateByUe.size;
  const mappedPct = templateSpotCount
    ? Math.round((configured / templateSpotCount) * 100)
    : 0;

  let state: BrandingDriftState;
  if (!hasTemplate) {
    state = 'unknown';
  } else if (orphaned > 0) {
    state = 'outdated';
  } else if (missing > 0) {
    state = 'behind';
  } else {
    state = 'up-to-date';
  }

  return {
    hasTemplate,
    templateVersion: activeTemplate?.version,
    templateSpotCount,
    configured,
    placeholders,
    missing,
    orphaned,
    newAvailable,
    removedPresent,
    mappedPct,
    state,
    behindCount: missing,
    rows,
  };
};

/* ============================================================ trigger drift */

/** Per-trigger drift status, mirroring the spot statuses on the script side. */
export type TriggerDriftStatus = 'scripted' | 'no-script' | 'new' | 'orphaned';

/** AI-trigger region type as reported by the pixel-streaming template. */
export const AI_TRIGGER_REGION_TYPE = 'MytaAITrigger';

/** Minimal assistant-script shape the trigger-drift engine reads. */
export interface IDriftScript {
  scriptId?: string;
  scriptTrigger: string | string[];
  triggerType?: string;
  mapName?: string;
}

export interface ITriggerDriftRow<TScript extends IDriftScript = IDriftScript> {
  /** Canonical region/trigger name (or the script's primary trigger when orphaned). */
  trigger: string;
  status: TriggerDriftStatus;
  isNew: boolean;
  regionType?: string;
  scriptId?: string | null;
  script?: TScript;
}

export interface ITriggerDrift<TScript extends IDriftScript = IDriftScript> {
  hasTemplate: boolean;
  templateVersion?: string;
  total: number;
  scripted: number;
  noScript: number;
  newCount: number;
  orphaned: number;
  rows: ITriggerDriftRow<TScript>[];
}

const triggersOf = (script: IDriftScript): string[] =>
  (Array.isArray(script.scriptTrigger)
    ? script.scriptTrigger
    : [script.scriptTrigger]
  ).filter(Boolean);

/**
 * Region-canonical script drift. A region (AI trigger) is *covered* when a
 * script binds to its name, *new* when it appeared in the active version, and
 * a script is *orphaned* when none of its triggers resolve to any region/spot
 * in the active build. Single source of truth shared by the admin assistant
 * table and the hub editor.
 */
export const computeTriggerDrift = <TScript extends IDriftScript>(
  scripts: TScript[],
  activeTemplate?: IMapTemplateRecord | null,
  previousTemplate?: IMapTemplateRecord | null,
  options: { mapName?: string } = {},
): ITriggerDrift<TScript> => {
  const hasTemplate = Boolean(activeTemplate);
  const mapName = options.mapName?.toLowerCase();

  const scopedScripts = mapName
    ? scripts.filter((s) => (s.mapName ?? '').toLowerCase() === mapName)
    : scripts;

  const regions = (activeTemplate?.regions ?? []).filter(
    (region) => region.type === AI_TRIGGER_REGION_TYPE,
  );

  // Every token UE knows about in the active build: region names + spot ids +
  // the synthetic landing trigger. A script is orphaned only when none of its
  // triggers resolve to one of these.
  const templateTokens = new Set<string>();
  (activeTemplate?.regions ?? []).forEach((region) =>
    templateTokens.add(region.name.toLowerCase()),
  );
  (activeTemplate?.spots ?? []).forEach((spot) =>
    templateTokens.add(normalizeUe(spot.ueIdentifier).toLowerCase()),
  );
  templateTokens.add('landing');

  const previousRegionSet = new Set(
    (previousTemplate?.regions ?? []).map((region) =>
      region.name.toLowerCase(),
    ),
  );
  const newRegionNames = new Set<string>();
  if (previousTemplate) {
    regions.forEach((region) => {
      if (!previousRegionSet.has(region.name.toLowerCase())) {
        newRegionNames.add(region.name.toLowerCase());
      }
    });
  }

  const rows: ITriggerDriftRow<TScript>[] = [];
  let scripted = 0;
  let noScript = 0;
  let newCount = 0;

  regions
    .slice()
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    )
    .forEach((region) => {
      const script = scopedScripts.find((candidate) =>
        triggersOf(candidate).some(
          (trigger) => trigger.toLowerCase() === region.name.toLowerCase(),
        ),
      );
      const isNew = newRegionNames.has(region.name.toLowerCase());
      let status: TriggerDriftStatus;
      if (script) {
        status = 'scripted';
        scripted += 1;
      } else if (isNew) {
        status = 'new';
        newCount += 1;
      } else {
        status = 'no-script';
        noScript += 1;
      }
      rows.push({
        trigger: region.name,
        status,
        isNew,
        regionType: region.type,
        scriptId: script?.scriptId ?? null,
        script,
      });
    });

  let orphaned = 0;
  if (hasTemplate) {
    scopedScripts.forEach((script) => {
      const triggers = triggersOf(script);
      if (triggers.length === 0) {
        return;
      }
      const allGone = triggers.every(
        (trigger) => !templateTokens.has(trigger.toLowerCase()),
      );
      if (!allGone) {
        return;
      }
      orphaned += 1;
      rows.push({
        trigger: triggers[0] || script.scriptId || 'unknown',
        status: 'orphaned',
        isNew: false,
        scriptId: script.scriptId ?? null,
        script,
      });
    });
  }

  return {
    hasTemplate,
    templateVersion: activeTemplate?.version,
    total: regions.length,
    scripted,
    noScript,
    newCount,
    orphaned,
    rows,
  };
};
